import { useState, useEffect, useRef, useMemo } from 'react'
import { Canvas } from '@react-three/fiber'
import { OrbitControls, PerspectiveCamera } from '@react-three/drei'
import PuppetScene from './PuppetScene'
import ControlBarDisplay from './ControlBarDisplay'
import { GRAVITY_SCALE } from '../physics/constants'
import CameraTracker from './CameraTracker'
import StringPositionsDisplay from './StringPositionsDisplay'
import { useShowRunner } from '../show/useShowRunner'
import { getMotion } from '../show/namedMotions'
import { getTruthPose } from '../pose/truthPoses'
import type { Show } from '../show/types'
import '../App.css'

interface StringPosition {
  x: number
  y: number
  z: number
}

interface StringPositions {
  head?: StringPosition
  chest?: StringPosition
  leftHand?: StringPosition
  rightHand?: StringPosition
  leftShoulder?: StringPosition
  rightShoulder?: StringPosition
  leftFoot?: StringPosition
  rightFoot?: StringPosition
}

export default function Simulation() {
  const [controlBarState, setControlBarState] = useState({
    position: { x: 0, y: 2.5, z: 0 },
    rotation: { roll: 0, pitch: 0, yaw: 0 }
  })
  
  const [cameraState, setCameraState] = useState<{
    position: { x: number; y: number; z: number }
    rotation: { roll: number; pitch: number; yaw: number }
  } | null>(null)

  const [stringPositions, setStringPositions] = useState<{
    controller?: StringPositions
    stringStart?: StringPositions
    stringEnd?: StringPositions
    puppet?: StringPositions
  }>({})

  const [forcesTorques, setForcesTorques] = useState<{
    forces?: StringPositions
    torques?: StringPositions
  }>({})

  const [timing, setTiming] = useState<{
    simTime: number
    realTime: number
    fps: number
    realtimeRatio: number
  } | null>({ simTime: 0, realTime: 0, fps: 0, realtimeRatio: 0 })

  const [stringLengthState, setStringLengthState] = useState<{
    selectedIndex: number | null
    restLengths: Map<string, number>
  }>({ selectedIndex: null, restLengths: new Map() })

  const [topTelemetryOpen, setTopTelemetryOpen] = useState(true)
  const [bottomTelemetryOpen, setBottomTelemetryOpen] = useState(true)

  // Show mode: load script, play/stop, run timeline
  const [show, setShow] = useState<Show | null>(null)
  const [showPlaying, setShowPlaying] = useState(false)
  const showStartRealTimeRef = useRef<number>(0)
  useEffect(() => {
    const base = typeof import.meta.env.BASE_URL === 'string' ? import.meta.env.BASE_URL : '/'
    fetch(`${base}shows/punch-and-judy.json`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data: Show | null) => data && setShow(data))
      .catch(() => {})
  }, [])
  const showTime =
    showPlaying && timing != null
      ? Math.max(0, timing.realTime - showStartRealTimeRef.current)
      : null
  const showState = useShowRunner(show, showTime ?? 0)
  const idleMotion = getMotion('idle')
  const showSequence = showPlaying
    ? (showState.motion ?? idleMotion)
    : null
  const showMotionStartTime =
    showPlaying && (showState.motion ?? idleMotion)
      ? (showState.motion ? showState.motionStartTime : (showTime ?? 0))
      : 0
  const handlePlayShow = () => {
    showStartRealTimeRef.current = timing?.realTime ?? performance.now() / 1000
    setShowPlaying(true)
  }
  const handleStopShow = () => setShowPlaying(false)

  const meanErrorToTruth = useMemo(() => {
    if (!showPlaying || !showState.currentActionName) return null
    const truth = getTruthPose(showState.currentActionName)
    const puppet = stringPositions.puppet
    if (!truth || !puppet) return null
    const points: (keyof typeof truth)[] = [
      'head', 'chest', 'leftHand', 'rightHand', 'leftShoulder', 'rightShoulder', 'leftFoot', 'rightFoot'
    ]
    let sum = 0
    let n = 0
    for (const p of points) {
      const a = puppet[p as keyof typeof puppet]
      const b = truth[p]
      if (a && b) {
        sum += Math.sqrt((a.x - b.x) ** 2 + (a.y - b.y) ** 2 + (a.z - b.z) ** 2)
        n += 1
      }
    }
    return n === 0 ? null : sum / n
  }, [showPlaying, showState.currentActionName, stringPositions.puppet])

  const handleExportPose = () => {
    const payload = {
      timestamp: new Date().toISOString(),
      realTime: timing?.realTime,
      puppet: stringPositions.puppet ?? null,
      truth: (showPlaying && showState.currentActionName)
        ? getTruthPose(showState.currentActionName) ?? null
        : null,
      currentActionName: showPlaying ? showState.currentActionName : null,
      restLengths: Object.fromEntries(stringLengthState.restLengths),
      controlBar: controlBarState,
    }
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `pose-snapshot-${Date.now()}.json`
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="app">
      <div className={`telemetry-panel telemetry-panel-top ${topTelemetryOpen ? 'open' : 'closed'}`}>
        <button
          type="button"
          className="telemetry-tab"
          onClick={() => setTopTelemetryOpen((o) => !o)}
          title={topTelemetryOpen ? 'Hide telemetry' : 'Show telemetry'}
          aria-expanded={topTelemetryOpen}
        >
          Telemetry {topTelemetryOpen ? '▲' : '▼'}
        </button>
        <div className="telemetry-content">
          <ControlBarDisplay 
            controlBarPosition={controlBarState.position}
            controlBarRotation={controlBarState.rotation}
            cameraPosition={cameraState?.position}
            cameraRotation={cameraState?.rotation}
            timing={timing}
            showDialogue={showPlaying ? showState.dialogue : null}
            showTitle={showPlaying && show ? show.title : null}
            showCharacterName={
              show
                ? (id) => show.characters.find((c) => c.id === id)?.name ?? id
                : undefined
            }
            meanErrorToTruth={meanErrorToTruth}
            gravityScale={GRAVITY_SCALE}
          />
        </div>
      </div>
      <div className="show-controls" style={{ pointerEvents: 'auto' }}>
        {show && (
          !showPlaying ? (
            <button type="button" className="show-btn" onClick={handlePlayShow}>
              Play Punch & Judy
            </button>
          ) : (
            <button type="button" className="show-btn show-btn-stop" onClick={handleStopShow}>
              Stop show
            </button>
          )
        )}
        <button type="button" className="show-btn show-btn-export" onClick={handleExportPose} title="Download current pose + truth + rest lengths as JSON">
          Export pose
        </button>
      </div>
      <div className={`telemetry-panel telemetry-panel-bottom ${bottomTelemetryOpen ? 'open' : 'closed'}`}>
        <div className="telemetry-content">
          <StringPositionsDisplay
            controllerPositions={stringPositions.controller}
            stringStartPositions={stringPositions.stringStart}
            stringEndPositions={stringPositions.stringEnd}
            puppetPositions={stringPositions.puppet}
            truthPositions={
              showPlaying && showState.currentActionName
                ? getTruthPose(showState.currentActionName) ?? undefined
                : undefined
            }
            forces={forcesTorques.forces}
            torques={forcesTorques.torques}
            selectedStringIndex={stringLengthState.selectedIndex}
            stringRestLengths={stringLengthState.restLengths}
          />
        </div>
        <button
          type="button"
          className="telemetry-tab"
          onClick={() => setBottomTelemetryOpen((o) => !o)}
          title={bottomTelemetryOpen ? 'Hide positions' : 'Show positions'}
          aria-expanded={bottomTelemetryOpen}
        >
          Positions {bottomTelemetryOpen ? '▲' : '▼'}
        </button>
      </div>
      <div className="canvas-container">
        <Canvas shadows>
          <PerspectiveCamera 
            makeDefault 
            position={[-0.08, 1.32, 3.95]}
            rotation={[0.0471, -0.0227, 0.001745]} // Pitch: 2.7°, Yaw: -1.3°, Roll: 0.1° (converted to radians)
          />
          <ambientLight intensity={0.5} />
          <directionalLight
            position={[5, 10, 5]}
            intensity={1}
            castShadow
            shadow-mapSize-width={2048}
            shadow-mapSize-height={2048}
          />
          <PuppetScene 
            command="" 
            isExecuting={false}
            onControlBarStateChange={(pos, rot) => setControlBarState({ position: pos, rotation: rot })}
            onStringPositionsChange={(positions) => setStringPositions({
              controller: positions.controller,
              stringStart: positions.stringStart,
              stringEnd: positions.stringEnd,
              puppet: positions.puppet
            })}
            onForcesTorquesChange={(data) => setForcesTorques({ forces: data.forces, torques: data.torques })}
            onTimingUpdate={setTiming}
            onStringLengthStateChange={(selectedIndex, restLengths) =>
              setStringLengthState({ selectedIndex, restLengths })
            }
            showSequence={showSequence}
            showMotionStartTime={showMotionStartTime}
            showTime={showTime}
            showCurrentActionName={showPlaying ? showState.currentActionName : null}
          />
          <CameraTracker 
            onCameraStateChange={(pos, rot) => setCameraState({ position: pos, rotation: rot })}
          />
          <OrbitControls
            enablePan={true}
            enableZoom={true}
            enableRotate={true}
            minDistance={3}
            maxDistance={20}
          />
        </Canvas>
      </div>
    </div>
  )
}
